import { parseRoomLink } from "@codeshare/config";
import type { EditorHandle } from "@codeshare/editor";
import { type Detection, languages } from "@codeshare/languages";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { detectContent } from "../document/content-detector";
import { DocumentLanguage } from "../document/document-language";
import { downloadName, downloadText } from "../document/files";
import { SAMPLE_CODE, SAMPLE_PATH } from "../sample";
import { getIdentityStore } from "../session/identity";
import { zipWorkspace } from "./import-export";
import { WorkspaceSession } from "./workspace-session";

const UNTITLED = /^Untitled-\d+$/;
const SOURCES = new Set([
  "user",
  "filename",
  "extension",
  "shebang",
  "modeline",
  "content",
  "default",
]);

/** A language from the shared document (written by any peer) as a local Detection. */
function fromShared(lang: { id: string; source: string }): Detection {
  const source = (SOURCES.has(lang.source) ? lang.source : "default") as Detection["source"];
  return { id: lang.id, source, confidence: 1 };
}
const PLAIN: Detection = { id: "plaintext", source: "default", confidence: 0 };

// One workspace per page.
let session: WorkspaceSession | undefined;
export function getWorkspaceSession(): WorkspaceSession {
  // Opened from a room link: show the room's files once they arrive, not a sample to type into.
  const joining = parseRoomLink(location.href) !== undefined;
  session ??= new WorkspaceSession({
    profile: getIdentityStore().get(),
    persistLocally: true,
    ...(joining ? {} : { seed: { path: SAMPLE_PATH, content: SAMPLE_CODE } }),
  });
  return session;
}

/**
 * The editor's view of the workspace: which file is open, its language, and the file actions the
 * shell exposes. Works the same solo and in a room (the workspace is always a Y.Doc).
 */
export function useWorkspace() {
  const workspace = useMemo(getWorkspaceSession, []);
  const snapshot = useSyncExternalStore(
    (listener) => workspace.subscribe(listener),
    () => workspace.snapshot,
  );
  const handleRef = useRef<EditorHandle | undefined>(undefined);
  const languageRef = useRef<DocumentLanguage | undefined>(undefined);
  const [language, setLanguage] = useState<Detection>(PLAIN);
  const [editorReady, setEditorReady] = useState(false);

  const active = snapshot.files.find((f) => f.id === snapshot.activeId);
  const activePath = active?.path;

  // (Re)open the active file in the editor whenever it, its name, or the shared doc changes.
  const activeId = snapshot.activeId;
  const canWrite = snapshot.canWrite;
  // Free editor models of files that were deleted (by anyone) or belong to a previous room.
  // Declared before the effect that opens the active file, so it runs first: a new room's file
  // opens after the previous room's models are closed, not before.
  const fileIds = snapshot.files.map((f) => f.id).join(",");
  const docRef = useRef(workspace.doc);
  useEffect(() => {
    const handle = handleRef.current;
    if (!handle) return;
    if (docRef.current !== workspace.doc) {
      docRef.current = workspace.doc;
      handle.closeAllSharedDocuments();
      return;
    }
    const live = new Set(fileIds.split(","));
    for (const id of knownIds.current) if (!live.has(id)) handle.closeSharedDocument(id);
    knownIds.current = live;
  }, [fileIds, workspace]);
  const knownIds = useRef(new Set<string>());

  useEffect(() => {
    const handle = handleRef.current;
    const entry = activeId ? workspace.workspace.get(activeId) : undefined;
    if (!editorReady || !handle || !entry || activePath === undefined) return;
    const untitled = UNTITLED.test(activePath);
    languageRef.current?.dispose();
    const id = entry.id;
    const stored = () => workspace.workspace.get(id)?.lang;
    const display = (language: Detection) => {
      setLanguage(language);
      void handle.setLanguage(language.id);
    };
    // Local detection; results are shared through the document so every peer renders the same
    // language. A manual choice (source "user") is shared too and pauses detection everywhere.
    const detector = new DocumentLanguage(
      {
        registry: languages,
        detectContent,
        onChange: (detection) => {
          const shared = stored();
          if (shared?.source === "user" && detection.source !== "user") return;
          if (
            workspace.workspace.canWrite &&
            (shared?.id !== detection.id || shared?.source !== detection.source)
          ) {
            workspace.workspace.setLanguage(id, { id: detection.id, source: detection.source });
          }
          display(detection);
        },
      },
      untitled ? undefined : activePath,
      entry.text.toString(),
    );
    languageRef.current = detector;
    const initial = stored();
    if (initial?.source === "user") detector.override(initial.id);
    else
      display(
        initial
          ? ({ ...detector.current, ...initial, confidence: 1 } as Detection)
          : detector.current,
      );

    // Someone else changed the language.
    const offFile = workspace.workspace.observeFile(id, (keys) => {
      if (!keys.has("lang")) return;
      const shared = stored();
      if (shared?.source === "user") {
        if (detector.current.source !== "user" || detector.current.id !== shared.id)
          detector.override(shared.id);
      } else if (detector.current.source === "user") {
        detector.override(null); // they switched back to automatic
      } else if (shared) {
        display(fromShared(shared));
      }
    });
    void handle.setSharedDocument({
      fileId: entry.id,
      path: activePath,
      text: entry.text,
      awareness: workspace.awareness,
      language: (stored() ?? detector.current).id,
    });
    handle.editor.updateOptions({ readOnly: !canWrite });
    const onText = () => detector.contentChanged(entry.text.toString());
    entry.text.observe(onText);
    return () => {
      entry.text.unobserve(onText);
      offFile();
      detector.dispose();
    };
  }, [editorReady, workspace, activeId, activePath, canWrite]);

  const attach = useCallback((handle: EditorHandle) => {
    handleRef.current = handle;
    setEditorReady(true);
  }, []);

  const nextUntitled = () => {
    const taken = new Set(workspace.snapshot.files.map((f) => f.path));
    let n = 1;
    while (taken.has(`Untitled-${n}`)) n++;
    return `Untitled-${n}`;
  };

  return {
    files: snapshot.files,
    activeId: snapshot.activeId,
    name: activePath ?? "",
    untitled: activePath ? UNTITLED.test(activePath) : true,
    canWrite: snapshot.canWrite,
    /** The room's toolchain choices by runner (unvalidated: they come from peers). */
    toolchains: snapshot.toolchains,
    setToolchain: (runnerId: string, toolchain: string | undefined) =>
      workspace.setToolchain(runnerId, toolchain),
    language,
    session: workspace,
    attach,
    select: (id: string) => workspace.open(id),
    deleteFile: (id: string) => workspace.delete(id),
    renameFile: (id: string, path: string) => workspace.rename(id, path),
    moveFile: (id: string, index: number) => workspace.move(id, index),
    addFile: (path: string) => workspace.addFile(path),
    importFiles: (files: { path: string; text: string }[]) => workspace.importFiles(files),
    downloadAll: async () => {
      const bytes = await zipWorkspace(workspace.contents());
      const url = URL.createObjectURL(
        new Blob([bytes as Uint8Array<ArrayBuffer>], { type: "application/zip" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = "codescreen-workspace.zip";
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    },
    newUntitled: () => workspace.addFile(nextUntitled()),
    open: (name: string, content: string) => workspace.addFile(name, content),
    rename: (name: string) => {
      if (snapshot.activeId) workspace.rename(snapshot.activeId, name);
    },
    setLanguageOverride: (languageId: string | null) => {
      const id = snapshot.activeId;
      if (!id || !workspace.workspace.canWrite) return;
      // Shared first, so the detector's change handler sees the new state.
      workspace.workspace.setLanguage(
        id,
        languageId ? { id: languageId, source: "user" } : undefined,
      );
      languageRef.current?.override(languageId);
    },
    download: () => {
      const entry = workspace.active;
      if (!entry) return;
      downloadText(
        downloadName(entry.path, UNTITLED.test(entry.path), language.id),
        entry.text.toString(),
      );
    },
  };
}
